import { config } from "../config/config";
import * as auth from "../auth";
import { workingDays } from "shared/src/calendar.mjs";
import { companyTagGroup } from "shared/src/client.mjs";
import { getFileExtension, getFilename } from "shared/src/file.mjs";
import { status } from "shared/src/appointment.mjs";
import { role } from "shared/src/roles.mjs";
import {
  currentLocationFromCurrentUrl,
  currentLocationFromRequest,
} from "../components/LocationSelector";
import { TREATMENT_STATE_INACTIVE } from "shared/src/company-tag.mjs";

/**
 * Syntax highlighting for gql in vscode
 * @see {@link https://stackoverflow.com/questions/60643685/there-is-a-way-to-syntax-highlight-graphql-typedef-inside-grave-accent-in}
 */
const gql = String.raw;

/**
 * Shared input types for API functions
 *
 * Aliases to shared GraphQL input types
 * @typedef {import('shared/src/gql-types.mjs').AppointmentConnectionFilterInput} AppointmentFilter
 * @typedef {import('shared/src/gql-types.mjs').AppointmentConnectionOrderInput} AppointmentOrder
 * @typedef {import('shared/src/gql-types.mjs').CalendarConnectionFilterInput} CalendarFilter
 * @typedef {import('shared/src/gql-types.mjs').NameDayConnectionFilterInput} NameDayFilter
 * @typedef {import('shared/src/gql-types.mjs').EmployeeConnectionFilterInput} EmployeeFilter
 * @typedef {import('shared/src/gql-types.mjs').EmployeeConnectionOrderInput} EmployeeOrder
 * @typedef {import('shared/src/gql-types.mjs').PageInput} PageInput
 * @typedef {import('shared/src/gql-types.mjs').DateFilterInput} DateFilterInput
 * @typedef {import('shared/src/gql-types.mjs').LocationBlocationConnectionFilterInput} BlocationFilter
 * @typedef {import('shared/src/gql-types.mjs').LocationSettingsConnectionFilterInput} LocationSettingsFilter
 * @typedef {import('shared/src/gql-types.mjs').LocationServiceConnectionFilterInput} LocationServiceFilter
 * @typedef {import('shared/src/gql-types.mjs').AuditConnectionFilterInput} AuditFilter
 * @typedef {import('shared/src/gql-types.mjs').ClientConnectionFilterInput} ClientFilter
 * @typedef {import('shared/src/gql-types.mjs').ProductFilterInput} ProductFilter
 * @typedef {import('shared/src/gql-types.mjs').ServiceConnectionFilterInput} ServiceFilter
 * @typedef {import('shared/src/gql-types.mjs').AppointmentSuggestionsFilterInput} AppointmentSuggestionsFilter
 * @typedef {import('shared/src/gql-types.mjs').AppointmentSuggestionsDaysFilterInput} AppointmentSuggestionsDaysFilter
 * @typedef {import('shared/src/gql-types.mjs').FilledServiceTemplateFilterInput} FilledServiceTemplateFilterInput
 * @typedef {import('shared/src/gql-types.mjs').ServiceUsageReportParams} ServiceUsageReportParams
 *
 * Mutation/input payloads
 * @typedef {import('shared/src/gql-types.mjs').PatchAppointmentInput} PatchAppointmentInput
 * @typedef {import('shared/src/gql-types.mjs').CreateAppointmentInput} CreateAppointmentInput
 * @typedef {import('shared/src/gql-types.mjs').CreateEmployeeBreakInput} CreateEmployeeBreakInput
 * @typedef {import('shared/src/gql-types.mjs').DeleteEmployeeBreakInput} DeleteEmployeeBreakInput
 * @typedef {import('shared/src/gql-types.mjs').CreateCalendarInput} CreateCalendarInput
 * @typedef {import('shared/src/gql-types.mjs').DeleteAppointmentInput} DeleteAppointmentInput
 * @typedef {import('shared/src/gql-types.mjs').PatchServiceInput} PatchServiceInput
 * @typedef {import('shared/src/gql-types.mjs').PatchCalendarInput} PatchCalendarInput
 * @typedef {import('shared/src/gql-types.mjs').PatchLocationInput} PatchLocationInput
 * @typedef {import('shared/src/gql-types.mjs').CreateLocationClosureInput} CreateLocationClosureInput
 * @typedef {import('shared/src/gql-types.mjs').PatchLocationClosureInput} PatchLocationClosureInput
 * @typedef {import('shared/src/gql-types.mjs').CreateLocationBlocationInput} CreateLocationBlocationInput
 * @typedef {import('shared/src/gql-types.mjs').PatchLocationBlocationInput} PatchLocationBlocationInput
 * @typedef {import('shared/src/gql-types.mjs').PatchLocationSettingsInput} PatchLocationSettingsInput
 * @typedef {import('shared/src/gql-types.mjs').PatchWebhookConfigInput} PatchWebhookConfigInput
 * @typedef {import('shared/src/gql-types.mjs').CreateServiceTemplateInput} CreateServiceTemplateInput
 * @typedef {import('shared/src/gql-types.mjs').PatchServiceTemplateInput} PatchServiceTemplateInput
 * @typedef {import('shared/src/gql-types.mjs').CreateServiceInput} CreateServiceInput
 * @typedef {import('shared/src/gql-types.mjs').CreateEditedMedicalReportInput} CreateEditedMedicalReportInput
 * @typedef {import('shared/src/gql-types.mjs').CreateAppointmentNoteInput} CreateAppointmentNoteInput
 * @typedef {import('shared/src/gql-types.mjs').CreateClientActivityLogInput} CreateClientActivityLogInput
 * @typedef {import('shared/src/gql-types.mjs').ReplaceCompanyTagGroupInput} ReplaceCompanyTagGroupInput
 * @typedef {import('shared/src/gql-types.mjs').PatchClientInput} PatchClientInput
 * @typedef {import('shared/src/gql-types.mjs').CreateFilledServiceTemplateInput} CreateFilledServiceTemplateInput
 * @typedef {import('shared/src/gql-types.mjs').PatchFilledServiceTemplateInput} PatchFilledServiceTemplateInput

 * Common entity and scalar types (imported once, reused across JSDoc)
 * @typedef {import('shared/src/gql-types.mjs').Appointment} Appointment
 * @typedef {import('shared/src/gql-types.mjs').Calendar} Calendar
 * @typedef {import('shared/src/gql-types.mjs').Client} Client
 * @typedef {import('shared/src/gql-types.mjs').Audit} Audit
 * @typedef {import('shared/src/gql-types.mjs').Service} Service
 * @typedef {import('shared/src/gql-types.mjs').Location} Location
 * @typedef {import('shared/src/gql-types.mjs').LocationBlocation} LocationBlocation
 * @typedef {import('shared/src/gql-types.mjs').JSON} JSON
 * @typedef {import('shared/src/gql-types.mjs').AppointmentStep} AppointmentStep
 * @typedef {import('shared/src/gql-types.mjs').AppointmentEventType} AppointmentEventType
 */

/**
 * Shared GraphQL output shapes
 */
/**
 * @template TItem
 * @typedef {{ data: Array<TItem> }} GqlList
 */
/**
 * @template TItem
 * @typedef {{ page?: { limit: number, offset: number }, count?: number, data: Array<TItem> }} GqlConnection
 */

export function contextFromRequest(request) {
  return { location: currentLocationFromRequest(request) };
}

export function contextFromLocation(location) {
  return { location };
}

/**
 * @typedef Context
 * @property {string} location
 *
 * @param {string} path
 * @param {RequestInit} [opts]
 * @param {Context} [context]
 */
async function apiFetch(path, opts, context) {
  const authToken = auth.getAuthenticationToken();

  const resp = await fetch(`${config.apiUrl}${path}`, {
    ...opts,
    headers: {
      ...opts?.headers,
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      "x-location": context?.location ?? currentLocationFromCurrentUrl(),
    },
  });

  if (resp.ok) {
    return resp.json();
  } else if (resp.status === 401) {
    auth.reAuthenticate();
    throw new Response("Unauthorized", { status: 401 });
  } else {
    throw new Error(`Failed to load resource at path ${path}.`);
  }
}

function workingHours(selection) {
  return workingDays.map((day) => day + " {" + selection + "}").join(" ");
}

/**
 * Load reception seats appointments and calendars
 * @param {{ filter: AppointmentFilter, order?: AppointmentOrder, calendarFilter?: CalendarFilter }} payload
 * @param {Context} context
 * @returns {Promise<{ data: { appointments: GqlConnection<Appointment>, calendars: GqlList<Calendar> } }>}
 */
export async function loadReceptionSeatsData(payload, context) {
  return loadGraphql(
    {
      query: gql`
      query loadReceptionSeatsData(
        $filter: AppointmentConnectionFilterInput!
        $order: AppointmentConnectionOrderInput
        $calendarFilter: CalendarConnectionFilterInput
        $location_id: ID!
      ) {
        appointments(filter: $filter, order: $order) {
          data {
            id
            start
            end
            status_id
            active_step
            actual_calendar_id
            group_id
            doctors {
              first_name
              last_name
            }
            any_doctor
            client {
              first_name
              last_name
              languageInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.LANGUAGES}" } }) {
                value
              }
              doctors {
                first_name
                last_name
              }
            }
            service {
              color
              name
              locationService(location_id: $location_id) {
                requires_doctor
              }
            }
            employee {
              first_name
              last_name
            }
            audit(
              filter: {
                or: [
                  { action: { eq: "I" } }
                  {
                    changed_fields: { name: "status_id", expr: { null: false } }
                  }
                  {
                    changed_fields: {
                      name: "active_step"
                      expr: { null: false }
                    }
                  }
                ]
              }
            ) {
              data {
                created_at
                changed_fields
                row_data
                action
              }
            }
          }
        }
        calendars(filter: $calendarFilter) {
          data {
            id
            title: name
            location_id
            service_type
          }
        }
      }
    `,
      operationName: "loadReceptionSeatsData",
      variables: payload,
    },
    null,
    context,
  );
}

/**
 * Load grouped appointments
 * @param {{ filter: AppointmentFilter, order?: AppointmentOrder }} payload
 * @returns {Promise<{ data: { appointments: GqlConnection<Appointment> } }>}
 */
export async function loadGroupAppointments(payload) {
  return loadGraphql({
    query: gql`
      query loadGroupAppointments(
        $filter: AppointmentConnectionFilterInput!
        $order: AppointmentConnectionOrderInput
      ) {
        appointments(filter: $filter, order: $order) {
          data {
            id
            start
            end
            status_id
            parent_id
            client {
              id
              first_name
              last_name
            }
            service {
              id
              color
              name
              type
              product_id
            }
          }
        }
      }
    `,
    operationName: "loadGroupAppointments",
    variables: payload,
  });
}

/**
 * Load appointments with duration for a location and calendars list
 * @param {{ filter: AppointmentFilter, order?: AppointmentOrder, calendarFilter?: CalendarFilter, location_id: string }} payload
 * @returns {Promise<{ data: { appointments: GqlConnection<Appointment>, calendars: GqlList<Calendar> } }>}
 */
export async function loadAppointments(payload, opts) {
  return loadGraphql(
    {
      query: gql`
        query loadAppointments(
          $filter: AppointmentConnectionFilterInput!
          $order: AppointmentConnectionOrderInput
          $calendarFilter: CalendarConnectionFilterInput
          $location_id: ID!
        ) {
          appointments(filter: $filter, order: $order) {
            data {
              id
              calendar_id
              start
              end
              status_id
              active_step
              parent_id
              actual_calendar_id
              reservation_id
              group_id
              location_id
              client {
                id
                first_name
                last_name
                birth_number
                phone
                activeTreatments: treatments(filter: {state: {neq: "${TREATMENT_STATE_INACTIVE}"}}) {
                  product_id
                }
              }
              service {
                id
                color
                name
                type
                product_id
                locationService(location_id: $location_id) {
                  duration
                  real_duration
                }
              }
              employee {
                id
                first_name
                last_name
              }
              adhoc_type
              audit {
                data {
                  created_at
                  changed_fields
                  row_data
                }
              }
            }
          }
          calendars(filter: $calendarFilter) {
            data {
              id
              title: name
              location_id
              service_type
            }
          }
        }
      `,
      operationName: "loadAppointments",
      variables: payload,
    },
    opts,
  );
}

/**
 * Load appointments with client notes and name days
 * @param {{ filter: AppointmentFilter, order?: AppointmentOrder, location_id: string, nameDaysFilter?: NameDayFilter }} payload
 * @returns {Promise<{ data: { appointments: GqlConnection<Appointment>, nameDays: GqlList<{ name: string }> } }>}
 */
export async function loadAppointmentsWithNotes(payload) {
  return loadGraphql({
    query: gql`
      query loadAppointmentsWithNotes(
        $filter: AppointmentConnectionFilterInput!
        $order: AppointmentConnectionOrderInput
        $location_id: ID!
        $nameDaysFilter: NameDayConnectionFilterInput
        $locationSettingsFilter: LocationSettingsConnectionFilterInput!
      ) {
        appointments(filter: $filter, order: $order) {
          data {
            id
            start
            end
            status_id
            parent_id
            reservation_id
            group_id
            followup_id
            client {
              id
              first_name
              last_name
              email
              birth_number
              phone
              birthdate
              prefs
              doctors {
                id
                first_name
                last_name
              }
              location_id
              labs_id
              any_doctor
              languageInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.LANGUAGES}" } }) {
                value
              }
              importantInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.IMPORTANT_INFO}" } }) {
                value
              }
              nextAppointmentNotes(filter: { active: { eq: true } }) {
                note
              }
              blocker
              blockers {
                text
                file {
                  type
                  objectid
                  file
                  details {
                    name
                  }
                }
              }
              virtualCareTreatments: treatments(filter: {virtual_care: {eq: true} end: {null: true}}) {
                plans {
                  link
                }
              }
              activeTreatments: treatments(filter: {state: {neq: "${TREATMENT_STATE_INACTIVE}"}}) {
                product_id
              }
            }
            service {
              id
              color
              name
              type
              product_id
              locationService(location_id: $location_id) {
                duration
                real_duration
              }
            }
            adhoc_type
            audit {
              data {
                created_at
                changed_fields
                row_data
              }
            }
          }
        }
        nameDays(filter: $nameDaysFilter) {
          data {
            name
          }
        }
        locationSettings(filter: $locationSettingsFilter) {
          data
        }
      }
    `,
    operationName: "loadAppointmentsWithNotes",
    variables: payload,
  });
}

/**
 * Load calendar appointments and blocations
 * @param {{ filter: AppointmentFilter, blocationFilter: BlocationFilter }} payload
 * @param {Context} context
 * @returns {Promise<{ data: { appointments: GqlConnection<Appointment>, locationBlocations: GqlList<LocationBlocation> } }>}
 */
export async function loadCalendarAppointments(payload, context) {
  return loadGraphql(
    {
      query: gql`
      query loadCalendarAppointments(
        $now: String!
        $filter: AppointmentConnectionFilterInput!
        $blocationFilter: LocationBlocationConnectionFilterInput!
        $location_id: ID!
      ) {
        appointments(filter: $filter) {
          data {
            id
            calendar_id
            start
            end
            status_id
            reservation_id
            doctors {
              first_name
              last_name
            }
            any_doctor
            client {
              id
              first_name
              last_name
              birthdate
              risks
              medicaments
              prefs
              labs_id
              location {
                name
              }
              languageInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.LANGUAGES}" } }) {
                value
              }
              importantInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.IMPORTANT_INFO}" } }) {
                value
              }                
              doctors {
                first_name
                last_name
              }
              any_doctor
              problematicTreatments: treatments(filter: {problematic: {eq: true} end: {null: true} expires_at: {gt: $now}}) {
                plans {
                  link
                }
              }
              virtualCareTreatments: treatments(filter: {virtual_care: {eq: true} end: {null: true} }) {
                plans {
                  link
                }
              }
              nextAppointmentNotes(filter: { active: { eq: true } }) {
                note
              }
            }
            service {
              id
              color
              name
              product_id
              locationService(location_id: $location_id) {
                requires_doctor
              }
            }
            audit {
              data {
                created_at
                changed_fields
              }
            }
          }
        }
        locationBlocations(filter: $blocationFilter) {
          data {
            title
            from
            to
            calendars {
              calendar_id
            }
          }
        }
      }
    `,
      operationName: "loadCalendarAppointments",
      variables: payload,
    },
    null,
    context,
  );
}

/**
 * Load floor manager appointments with audits and blocations
 * @param {{ filter: AppointmentFilter, order?: AppointmentOrder, auditFilter?: AuditFilter, location_id: string, blocationFilter: BlocationFilter }} payload
 * @param {Context} context
 * @returns {Promise<{ data: { appointments: GqlConnection<Appointment>, locationBlocations: GqlList<LocationBlocation> } }>}
 */
export async function loadFlooreManagerAppointments(payload, context) {
  return loadGraphql(
    {
      query: gql`
      query loadFlooreManagerAppointments(
        $now: String!
        $blocationFilter: LocationBlocationConnectionFilterInput!
        $filter: AppointmentConnectionFilterInput!
        $order: AppointmentConnectionOrderInput
        $auditFilter: AuditConnectionFilterInput
        $location_id: ID!
      ) {
        appointments(filter: $filter, order: $order) {
          data {
            id
            calendar_id
            location_id
            actual_calendar_id
            start
            end
            status_id
            adhoc_type
            parent_id
            group_id
            reservation_id
            doctors {
              first_name
              last_name
            }
            any_doctor
            client {
              id
              first_name
              last_name
              birthdate
              risks
              medicaments
              prefs
              labs_id
              location {
                name
              }
              languageInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.LANGUAGES}" } }) {
                value
              }
              importantInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.IMPORTANT_INFO}" } }) {
                value
              }  
              doctors {
                first_name
                last_name
              }
              any_doctor
              problematicTreatments: treatments(filter: {problematic: {eq: true} end: {null: true} expires_at: {gt: $now}}) {
                plans {
                  link
                }
              }
              virtualCareTreatments: treatments(filter: {virtual_care: {eq: true} end: {null: true}}) {
                plans {
                  link
                }
              }
              nextAppointmentNotes(filter: { active: { eq: true } }) {
                note
              } 
            }
            service {
              id
              color
              name
              type
              product_id
              locationService(location_id: $location_id) {
                real_duration
                actual_calendar
                requires_doctor
              }
            }
            audit(filter: $auditFilter) {
              data {
                created_at
                changed_fields
                row_data
                action
              }
            }
            employee {
              id
              first_name
              last_name
            }
            active_step
          }
        }
        locationBlocations(filter: $blocationFilter) {
          data {
            title
            from
            to
            calendars {
              calendar_id
            }
          }
        }
      }
    `,
      operationName: "loadFlooreManagerAppointments",
      variables: payload,
    },
    null,
    context,
  );
}

/**
 * Load details about client's last appointment for context
 * @param {{ lastAppointmentFilter: AppointmentFilter }} payload
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { lastAppointments: GqlConnection<Appointment> } }>}
 */
export function loadAppointmentDetail(payload, opts) {
  return loadGraphql(
    {
      query: gql`
  query loadAppointmentDetail($lastAppointmentFilter: AppointmentConnectionFilterInput! $treatmentsFilter: ClientTreatmentsFilter! $client_id: ID!) {
          client(id: $client_id) {
            treatments(filter: $treatmentsFilter) {
              product {
                name
              }
              plans {
                link
              }
            }
          }
          lastAppointments: appointments(
            filter: $lastAppointmentFilter
            page: { offset: 0, limit: 1 }
            order: { desc: treated }
          ) {
            data {
              id
              start
              service {
                name
              }
              employeeAssignmentAudits: audit(
                filter: {
                  and: [
                    {
                      changed_fields: {
                        name: "employee_id"
                        expr: { null: false }
                      }
                    }
                    {
                      or: [
                        {
                          changed_fields: { name: "status_id", expr: { eq: "${status.ASSIGNED}" } }
                        },
                        {
                          row_data: { name: "status_id", expr: { eq: "${status.ASSIGNED}" } }
                        }
                      ]
                    }
                  ]
                }
                order: { desc: created_at }
                page: { offset: 0, limit: 1 }
              ) {
                data {
                  changed_fields
                }
              }
            }
          }
        }
      `,
      operationName: "loadAppointmentDetail",
      variables: payload,
    },
    opts,
  );
}

/**
 * @param {string[]} ids
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { employees: Array<{ first_name: string, last_name: string }> } }>}
 */
export function loadEmployeeNameData(ids, opts) {
  if (ids.length === 0) {
    return { data: { employees: { data: [] } } };
  }

  const filter = { id: { in: ids } };
  const payload = { filter };

  return loadGraphql(
    {
      query: gql`
        query loadEmployeeNameData($filter: EmployeeConnectionFilterInput) {
          employees(filter: $filter) {
            data {
              first_name
              last_name
            }
          }
        }
      `,
      operationName: "loadEmployeeNameData",
      variables: payload,
    },
    opts,
  );
}

/**
 * Patch an appointment
 * @param {PatchAppointmentInput} payload
 * @returns {Promise<{ data: { patchAppointment: JSON } }>}
 */
export function patchAppointment(payload) {
  return loadGraphql({
    query: gql`
      mutation patchAppointment($input: PatchAppointmentInput!) {
        patchAppointment(input: $input)
      }
    `,
    operationName: "patchAppointment",
    variables: { input: payload },
  });
}

/**
 * Patch multiple appointments
 * @param {Array<PatchAppointmentInput>} payload
 * @returns {Promise<{ data: { patchAppointments: Array<JSON> } }>}
 */
export function patchAppointments(payload) {
  return loadGraphql({
    query: gql`
      mutation patchAppointments($input: [PatchAppointmentInput!]!) {
        patchAppointments(input: $input)
      }
    `,
    operationName: "patchAppointments",
    variables: { input: payload },
  });
}

/**
 * Create appointment
 * @param {CreateAppointmentInput} payload
 * @returns {Promise<{ data: { createAppointment: { id: string } } }>}
 */
export function createAppointment(payload) {
  return loadGraphql({
    query: gql`
      mutation createAppointment($input: CreateAppointmentInput!) {
        createAppointment(input: $input) {
          id
        }
      }
    `,
    operationName: "createAppointment",
    variables: { input: payload },
  });
}

export function createAppointments(payload) {
  return loadGraphql({
    query: gql`
      mutation createAppointments($input: [CreateAppointmentInput!]!) {
        createAppointments(input: $input)
      }
    `,
    operationName: "createAppointments",
    variables: { input: payload },
  });
}

/**
 * Create employee break
 * @param {CreateEmployeeBreakInput} payload
 * @returns {Promise<{ data: { createEmployeeBreak: JSON } }>}
 */
export function createEmployeeBreak(payload) {
  return loadGraphql({
    query:
      "mutation createEmployeeBreak($input: CreateEmployeeBreakInput!) {createEmployeeBreak(input: $input)}",
    operationName: "createEmployeeBreak",
    variables: { input: payload },
  });
}

/**
 * Delete employee break
 * @param {DeleteEmployeeBreakInput} payload
 * @returns {Promise<{ data: { deleteEmployeeBreak: JSON } }>}
 */
export function deleteEmployeeBreak(payload) {
  return loadGraphql({
    query:
      "mutation deleteEmployeeBreak($input: DeleteEmployeeBreakInput!) {deleteEmployeeBreak(input: $input)}",
    operationName: "deleteEmployeeBreak",
    variables: { input: payload },
  });
}

/**
 * Create calendar
 * @param {CreateCalendarInput} payload
 * @returns {Promise<{ data: { createCalendar: { id: string } } }>}
 */
export function createCalendar(payload) {
  return loadGraphql({
    query:
      "mutation createCalendar($input: CreateCalendarInput!) {createCalendar(input: $input) {id}}",
    operationName: "createCalendar",
    variables: { input: payload },
  });
}

/**
 * Delete appointment
 * @param {DeleteAppointmentInput} payload
 * @returns {Promise<{ data: { deleteAppointment: { id: string } } }>}
 */
export function deleteAppointment(payload) {
  return loadGraphql({
    query:
      "mutation deleteAppointment($input: DeleteAppointmentInput!) {deleteAppointment(input: $input) {id} }",
    operationName: "deleteAppointment",
    variables: { input: payload },
  });
}

/**
 * Load location and calendars for a day
 * @param {{ calendarFilter?: CalendarFilter, location_id: string, day: string }} variables
 * @param {Conteext} context
 * @returns {Promise<{ data: { location: Location, calendars: GqlList<Calendar> } }>}
 */
export async function loadCalendarData(variables, context) {
  return loadGraphql(
    {
      query: gql`query loadCalendarData($calendarFilter: CalendarConnectionFilterInput, $location_id: ID!, $day: String!) {
      location(id: $location_id) {
        id name working_hours {${workingHours("valid_from valid_to from to")}}
        buffers(day: $day) {calendar_id, from, to}
      }
      calendars(filter: $calendarFilter) {data {id title: name location_id service_type}}
    }`,
      operationName: "loadCalendarData",
      variables: variables,
    },
    null,
    context,
  );
}

/**
 * Load floor manager calendar data for a day
 * @param {{ calendarFilter?: CalendarFilter, location_id: string, day: string }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { location: Location, calendars: GqlList<Calendar> } }>}
 */
export async function loadFloorManagerCalendarData(variables, context) {
  return loadGraphql(
    {
      query: gql`query loadFloorManagerCalendarData($calendarFilter: CalendarConnectionFilterInput, $location_id: ID!, $day: String!) {
      location(id: $location_id) {
        id name working_hours {${workingHours("valid_from valid_to from to")}}
        blocations {valid_from valid_to days exceptional}
        closures {valid_from valid_to}
        buffers(day: $day) {calendar_id, from, to}
      }
      calendars(filter: $calendarFilter) {data {id title: name location_id service_type}}
    }`,
      operationName: "loadFloorManagerCalendarData",
      variables: variables,
    },
    null,
    context,
  );
}

/**
 * Load clients list
 * @param {{ page: PageInput, filter?: ClientFilter }} variables
 * @param {Context} context
 *
 * @returns {Promise<{ data: { clients: GqlConnection<Client> } }>}
 */
export function loadClients(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query loadClients(
          $page: PageInput!
          $filter: ClientConnectionFilterInput
        ) {
          clients(page: $page, filter: $filter) {
            page {
              limit
              offset
            }
            count
            data {
              id
              first_name
              last_name
              birth_number
              birthdate
              location_id
              location {
                name
              }
            }
          }
        }
      `,
      operationName: "loadClients",
      variables: variables,
    },
    null,
    context,
  );
}

/**
 * Load audit history
 * @param {{ page: PageInput, filter: AuditFilter }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { audit: GqlConnection<Audit> } }>}
 */
export function loadHistory(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query loadHistory(
          $page: PageInput!
          $filter: AuditConnectionFilterInput
        ) {
          audit(page: $page, filter: $filter) {
            page {
              limit
              offset
            }
            count
            data {
              id
              relid
              created_at
              user_name
              action
              row_data
              changed_fields
            }
          }
        }
      `,
      operationName: "loadHistory",
      variables,
    },
    null,
    context,
  );
}

/**
 * Load audit history with opts
 * @param {{ page: PageInput, filter: AuditFilter }} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { audit: GqlConnection<Audit> } }>}
 */
export function loadClientHistory(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query loadClientHistory(
          $page: PageInput!
          $filter: AuditConnectionFilterInput
        ) {
          audit(page: $page, filter: $filter) {
            page {
              limit
              offset
            }
            count
            data {
              id
              relid
              created_at
              user_name
              action
              row_data
              changed_fields
            }
          }
        }
      `,
      operationName: "loadClientHistory",
      variables,
    },
    opts,
  );
}

/**
 * Load services and their templates
 * @param {{ page: PageInput, filter?: ServiceFilter }} variables
 * @param {RequestInit} [opts]
 * @returns {Promise<{ data: { services: GqlConnection<Service> } }>}
 */
export function loadServicesWithTemplates(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query loadServicesWithTemplates(
          $page: PageInput
          $filter: ServiceConnectionFilterInput
        ) {
          services(page: $page, filter: $filter) {
            page {
              limit
              offset
            }
            count
            data {
              id
              name
              color
              visible
              templates {
                id
                version
                template
                service_id
              }
            }
          }
        }
      `,
      operationName: "loadServicesWithTemplates",
      variables,
    },
    opts,
  );
}

/**
 * Load calendars
 * @param {{ page: PageInput, filter?: CalendarFilter }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { calendars: GqlConnection<Calendar> } }>}
 */
export function loadCalendars(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query loadCalendars(
          $page: PageInput!
          $filter: CalendarConnectionFilterInput
        ) {
          calendars(page: $page, filter: $filter) {
            page {
              limit
              offset
            }
            count
            data {
              id
              name
              service_type
              visible
            }
          }
        }
      `,
      operationName: "loadCalendars",
      variables,
    },
    null,
    context,
  );
}

/**
 * Load calendar editable data
 * @param {{ id: string }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { calendar: { id: string, name: string, visible: boolean, location: { name: string } } } }>}
 */
export function loadCalendarEditData(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query calendar($id: ID!) {
          calendar(id: $id) {
            id
            name
            visible
            location {
              name
            }
          }
        }
      `,
      operationName: "calendar",
      variables,
    },
    null,
    context,
  );
}

/**
 * Load reservation settings edit data
 * @param {{ locationSettingsFilter: LocationSettingsFilter }} variables
 * @returns {Promise<{ data: {  locationSettings: { data: JSON } } }>}
 */
export function loadReservationLocationSettingsData(variables) {
  return loadGraphql({
    query: gql`
      query loadReservationLocationSettingsData(
        $locationSettingsFilter: LocationSettingsConnectionFilterInput!
      ) {
        locationSettings(filter: $locationSettingsFilter) {
          data
        }
      }
    `,
    operationName: "loadReservationLocationSettingsData",
    variables,
  });
}

/**
 * Load reservation settings edit data
 * @param {{ locationSettingsFilter: LocationSettingsFilter, locationServicesFilter?: LocationServiceFilter }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { locationServices: GqlList<{ duration: number, service_id: string, service: { name: string } }>, locationSettings: { data: JSON } } }>}
 */
export function loadReservationSettingsEditData(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query loadReservationSettingsEditData(
          $locationSettingsFilter: LocationSettingsConnectionFilterInput!
          $locationServicesFilter: LocationServiceConnectionFilterInput
        ) {
          locationServices(filter: $locationServicesFilter) {
            data {
              duration
              service_id
              service {
                name
              }
            }
          }
          locationSettings(filter: $locationSettingsFilter) {
            data
          }
        }
      `,
      operationName: "loadReservationSettingsEditData",
      variables,
    },
    null,
    context,
  );
}

/**
 * Load reservation settings and usage report
 * @param {{ serviceUsageReportParams: ServiceUsageReportParams, locationSettingsFilter: LocationSettingsFilter }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { serviceUsageReport: Array<{ week_num: number, from: string, to: string, usage: JSON, capacity: JSON, available_minutes: number }>, locationSettings: GqlList<{ data: JSON }> } }>}
 */
export function loadReservationSettingsData(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query loadReservationSettingsData(
          $serviceUsageReportParams: ServiceUsageReportParams!
          $locationSettingsFilter: LocationSettingsConnectionFilterInput!
        ) {
          serviceUsageReport(params: $serviceUsageReportParams) {
            week_num
            from
            to
            usage
            capacity
            available_minutes
          }
          locationSettings(filter: $locationSettingsFilter) {
            data
          }
        }
      `,
      operationName: "loadReservationSettingsData",
      variables,
    },
    null,
    context,
  );
}

/**
 * Load webhook config data
 * @param {Record<string, never>} variables
 * @returns {Promise<{ data: { webhookConfigs: GqlList<{ relid: string, on_insert?: boolean, on_update_columns?: string[], on_delete?: boolean, schema: string, table: string, available_columns?: string[] }> } }>}
 */
export function loadWebhookConfigData(variables) {
  return loadGraphql({
    query: gql`
      query loadWebhookConfigData {
        webhookConfigs {
          data {
            relid
            on_insert
            on_update_columns
            on_delete
            schema
            table
            available_columns
          }
        }
      }
    `,
    operationName: "loadWebhookConfigData",
    variables,
  });
}

/**
 * Patch webhook config
 * @param {PatchWebhookConfigInput} payload
 * @returns {Promise<{ data: { patchWebhookConfig: JSON | null } }>}
 */
export function patchWebhookConfigData(payload) {
  return loadGraphql({
    query: gql`
      mutation patchWebhookConfig($input: PatchWebhookConfigInput!) {
        patchWebhookConfig(input: $input)
      }
    `,
    operationName: "patchWebhookConfig",
    variables: { input: payload },
  });
}

/**
 * Create location closure
 * @param {CreateLocationClosureInput} payload
 * @returns {Promise<{ data: { createLocationClosure: JSON | null } }>}
 */
export function createLocationClosure(payload) {
  return loadGraphql({
    query: gql`
      mutation createlocationClosure($input: CreateLocationClosureInput!) {
        createLocationClosure(input: $input)
      }
    `,
    operationName: "createLocationClosure",
    variables: { input: payload },
  });
}

/**
 * Patch location closure
 * @param {PatchLocationClosureInput} payload
 * @returns {Promise<{ data: { patchLocationClosure: JSON | null } }>}
 */
export function patchLocationClosure(payload) {
  return loadGraphql({
    query: gql`
      mutation patchLocationClosure($input: PatchLocationClosureInput!) {
        patchLocationClosure(input: $input)
      }
    `,
    operationName: "patchLocationClosure",
    variables: { input: payload },
  });
}

/**
 * Delete location closure
 * @param {string} id
 * @returns {Promise<{ data: { deleteLocationClosure: JSON | null } }>}
 */
export function deleteLocationClosure(id) {
  return loadGraphql({
    query:
      "mutation deleteLocationClosure($id: ID!) {deleteLocationClosure(id: $id)}",
    operationName: "deleteLocationClosure",
    variables: { id },
  });
}

/**
 * Create location blocation
 * @param {CreateLocationBlocationInput} payload
 * @returns {Promise<{ data: { createLocationBlocation: { id: string } } }>}
 */
export function createLocationBlocation(payload) {
  return loadGraphql({
    query: gql`
      mutation createlocationBlocation($input: CreateLocationBlocationInput!) {
        createLocationBlocation(input: $input) {
          id
        }
      }
    `,
    operationName: "createLocationBlocation",
    variables: { input: payload },
  });
}

/**
 * Patch location blocation
 * @param {PatchLocationBlocationInput} payload
 * @returns {Promise<{ data: { patchLocationBlocation: { id: string } } }>}
 */
export function patchLocationBlocation(payload) {
  return loadGraphql({
    query: gql`
      mutation patchLocationBlocation($input: PatchLocationBlocationInput!) {
        patchLocationBlocation(input: $input) {
          id
        }
      }
    `,
    operationName: "patchLocationBlocation",
    variables: { input: payload },
  });
}

/**
 * Delete location blocation
 * @param {string} id
 * @returns {Promise<{ data: { deleteLocationBlocation: { id: string } } }>}
 */
export function deleteLocationBlocation(id) {
  return loadGraphql({
    query:
      "mutation deleteLocationBlocation($id: ID!) {deleteLocationBlocation(id: $id) {id} }",
    operationName: "deleteLocationBlocation",
    variables: { id },
  });
}

/**
 * Patch location settings
 * @param {PatchLocationSettingsInput} payload
 * @returns {Promise<{ data: { patchLocationSettings: JSON | null } }>}
 */
export function patchLocationSettings(payload) {
  return loadGraphql({
    query:
      "mutation patchLocationSettings($input: PatchLocationSettingsInput!) {patchLocationSettings(input: $input) }",
    operationName: "patchLocationSettings",
    variables: { input: payload },
  });
}

/**
 * Load location edit data
 * @param {{ id: string, calendarFilter?: CalendarFilter }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { location: { id: string, name: string, working_hours: Array<{ valid_from: string, valid_to: string, from: string, to: string }>, blocations: Array<{ id: string, title: string, valid_from: string, valid_to: string, from: string, to: string, days?: string, exceptional?: boolean, calendars: Array<{ calendar_id: string }> }>, closures: Array<{ id: string, valid_from: string, valid_to: string }> } | null, calendars: GqlList<{ id: string, name: string }> } }>}
 */
export function loadLocationEditData(variables, context) {
  return loadGraphql(
    {
      query: gql`query location($id: ID! $calendarFilter: CalendarConnectionFilterInput) { 
      location(id: $id) {
        id
        name
        working_hours {${workingHours("valid_from valid_to from to")}}
        blocations {
          id
          title
          valid_from
          valid_to
          from
          to
          days
          exceptional
          calendars {
            calendar_id
          }
        }
        closures {
          id
          valid_from
          valid_to
        }
      }
      calendars(filter: $calendarFilter) {
        data {
          id
          name
        }
      }
    }`,
      operationName: "loadLocationEditData",
      variables,
    },
    null,
    context,
  );
}

/**
 * Patch location
 * @param {PatchLocationInput} payload
 * @returns {Promise<{ data: { patchLocation: { id: string } } }>}
 */
export function patchLocation(payload) {
  return loadGraphql({
    query:
      "mutation patchLocation($input: PatchLocationInput!) {patchLocation(input: $input) {id} }",
    operationName: "patchLocation",
    variables: { input: payload },
  });
}

/**
 * Load locations paged
 * @param {{ page: PageInput }} variables
 * @param {FetchOptions} opts
 * @returns {Promise<{ data: { locations: GqlConnection<{ id: string, name: string }> } }>}
 */
export function loadLocations(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query locations(
          $page: PageInput!
          $filter: LocationConnectionFilterInput
        ) {
          locations(page: $page, filter: $filter) {
            page {
              limit
              offset
            }
            count
            data {
              id
              name
            }
          }
        }
      `,
      operationName: "locations",
      variables,
    },
    null,
    context,
  );
}

/**
 * Load company products by location
 * @param {{ location_id?: string }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { company: { products: Array<{ id: string, name: string }> } | null } }>}
 */
export function loadCompany(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query loadCompany($location_id: ID) {
          company(location_id: $location_id) {
            products {
              id
              name
            }
          }
        }
      `,
      operationName: "loadCompany",
      variables,
    },
    null,
    context,
  );
}

/**
 * Load product with services
 * @param {{ id: string }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { product: ({ id: string, name: string, services: Array<{ id: string, name: string, color?: string, type?: string }> }) | null } }>}
 */
export function loadProduct(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query loadProduct($id: ID!) {
          product(id: $id) {
            id
            name
            services {
              id
              name
              color
              type
            }
          }
        }
      `,
      operationName: "loadProduct",
      variables,
    },
    null,
    context,
  );
}

/**
 * @param {{query: string, operationName: string, variables: object}} params
 * @param {RequestInit} [opts]
 * @param {Context} [context]
 */
async function loadGraphql(params, opts, context) {
  return apiFetch(
    `/api`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
      ...opts,
    },
    context,
  );
}

export function loadRfidAuthData(company_id, day) {
  return apiFetch(`/authenticate-rfid?company_id=${company_id}&day=${day}`);
}

export function setNow(at, date) {
  return apiFetch("/set-now", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ at, date }),
  });
}

export function resetNow() {
  return apiFetch("/reset-now", {
    method: "POST",
  });
}

/**
 * Load basic client data
 * @param {{ id: string }} variables
 * @returns {Promise<{ data: { client: ({ id: string, first_name: string, last_name: string, email?: string, birth_number?: string, phone?: string, street?: string, city?: string, post_code?: string }) | null } }>}
 */
export function loadClientData(variables) {
  return loadGraphql({
    query: `
      query client($id: ID!) {
        client(id: $id) {
          id
          first_name
          last_name
          email
          birth_number
          phone
          street
          city
          post_code
        }
      }
    `,
    operationName: "client",
    variables,
  });
}

/**
 * Patch service
 * @param {PatchServiceInput} payload
 * @returns {Promise<{ data: { patchService: { id: string } } }>}
 */
export function patchService(payload) {
  return loadGraphql({
    query:
      "mutation patchService($input: PatchServiceInput!) {patchService(input: $input) {id} }",
    operationName: "patchService",
    variables: { input: payload },
  });
}

/**
 * Patch calendar
 * @param {PatchCalendarInput} payload
 * @returns {Promise<{ data: { patchCalendar: { id: string } } }>}
 */
export function patchCalendar(payload) {
  return loadGraphql({
    query:
      "mutation patchCalendar($input: PatchCalendarInput!) {patchCalendar(input: $input) {id} }",
    operationName: "patchCalendar",
    variables: { input: payload },
  });
}

/**
 * Search clients
 * @param {{page: PageInput, filter?: ClientFilter}} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { clients: GqlConnection<{ id: string, first_name: string, last_name: string, birth_number?: string, phone?: string, street?: string, city?: string, post_code?: string, location_id?: string, prefs?: JSON }> } }>}
 */
export function clientSearch(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query clients($page: PageInput!, $filter: ClientConnectionFilterInput) {
          clients(page: $page, filter: $filter) {
            page {
              limit
              offset
            }
            count
            data {
              id
              first_name
              last_name
              birth_number
              phone
              street
              city
              post_code
              location_id
              prefs
              any_doctor
              doctors {
                id
              }
            }
          }
        }
      `,
      operationName: "clients",
      variables: variables,
    },
    opts,
  );
}

/**
 * Load location service options
 * @param {{filter?: LocationServiceFilter}} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { locationServices: GqlList<{ duration: number, requires_doctor?: boolean, service: { id: string, name: string } }> } }>}
 */
export function locationServiceOptions(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query locationServiceOptions(
          $filter: LocationServiceConnectionFilterInput
        ) {
          locationServices(filter: $filter) {
            data {
              duration
              requires_doctor
              service {
                id
                name
              }
            }
          }
        }
      `,
      operationName: "locationServiceOptions",
      variables,
    },
    opts,
  );
}

/**
 * Fetch service detail
 * @param {{ id: string }} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { service: ({ id: string, name: string, color?: string, locationServices: Array<{ duration: number, location: { id: string, name: string } }> }) | null } }>}
 */
export function fetchServiceDetail(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query fetchServiceDetail($id: ID!, $location_id: ID!) {
          service(id: $id) {
            id
            name
            color
            locationService(location_id: $location_id) {
              duration
              requires_doctor
              location {
                id
                name
              }
            }
          }
        }
      `,
      operationName: "fetchServiceDetail",
      variables,
    },
    opts,
  );
}

/**
 * Load service edit data
 * @param {{ id: string, location_id: string, calendarFilter?: CalendarFilter }} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @param {Context} context
 * @returns {Promise<{ data: { service: { id: string, name: string, color?: string, create_reservation: boolean, change_reservation_to: boolean, requires_braces: boolean, type?: string, locationServices: Array<{ duration: number, real_duration?: number, requires_doctor: boolean, requires_scan: boolean, requires_rentgen: boolean, actual_calendar: JSON, location: { id: string, name: string } }>, templates: Array<{ id: string, version: number, template: JSON, service_id: string }> }, company: { locations: Array<{ id: string, name: string }> }, calendars: GqlList<{ id: string, name: string, location_id: string }> } }>}
 */
export function loadserviceEditData(variables, opts, context) {
  return loadGraphql(
    {
      query: gql`
        query loadserviceEditData(
          $id: ID!
          $location_id: ID!
          $calendarFilter: CalendarConnectionFilterInput
        ) {
          service(id: $id) {
            id
            name
            color
            create_reservation
            change_reservation_to
            requires_braces
            type
            locationServices {
              duration
              real_duration
              requires_doctor
              requires_scan
              requires_rentgen
              actual_calendar
              location {
                id
                name
              }
            }
            templates {
              id
              version
              template
              service_id
            }
          }
          company(location_id: $location_id) {
            locations {
              id
              name
            }
          }
          calendars(filter: $calendarFilter) {
            data {
              id
              name
              location_id
            }
          }
        }
      `,
      operationName: "loadserviceEditData",
      variables,
    },
    opts,
    context,
  );
}

/**
 * Load product options
 * @param {{filter?: ProductFilter}} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { products: GqlList<{ id: string, name: string }> } }>}
 */
export function productOptions(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query productOptions($filter: ProductFilterInput) {
          products(filter: $filter) {
            data {
              id
              name
            }
          }
        }
      `,
      operationName: "productOptions",
      variables,
    },
    opts,
  );
}

/**
 * Load location options
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { locations: GqlList<{ id: string, name: string, company_id: string }> } }>}
 */
export function locationOptions(opts) {
  return loadGraphql(
    {
      query: gql`
        query locations {
          locations(filter: { current_company: { eq: true } }) {
            data {
              id
              name
              company_id
            }
          }
        }
      `,
      operationName: "locations",
      variables: {},
    },
    opts,
  );
}

export function filteredLocationOptions(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query locations($filter: LocationConnectionFilterInput) {
          locations(filter: $filter) {
            data {
              id
              name
              company_id
            }
          }
        }
      `,
      operationName: "locations",
      variables: variables,
    },
    opts,
  );
}

/**
 * Load future appointments
 * @param {{filter: AppointmentFilter}} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { appointments: GqlList<{ id: string, start: string, service: { name: string } }> } }>}
 */
export function futureAppointments(variables, opts) {
  return loadGraphql(
    {
      query: `query futureAppointments($filter: AppointmentConnectionFilterInput!) {
      appointments(filter: $filter, order: {asc: start}) {
        data {
          id
          start
          service {
            name
          }
        }
      }
    }`,
      operationName: "futureAppointments",
      variables,
    },
    opts,
  );
}

/**
 * Load appointment suggestions
 * @param {{filter: AppointmentSuggestionsFilter}} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { appointmentSuggestions: GqlList<{ start: string, end: string, calendar_id: string, calendar: { name: string } }> } }>}
 */
export function appointmentSuggestions(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query appointmentSuggestions(
          $filter: AppointmentSuggestionsFilterInput!
        ) {
          appointmentSuggestions(filter: $filter) {
            data {
              start
              end
              calendar_id
              calendar {
                name
              }
              availableEmployees(filter: {role: "${role.Doctor}"}) {
                id
                first_name
                last_name
              }
            }
          }
        }
      `,
      operationName: "appointmentSuggestions",
      variables,
    },
    opts,
  );
}

/**
 * Load appointment suggestions days
 * @param {{filter: AppointmentSuggestionsDaysFilter}} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { appointmentSuggestionsDays: { days: Array<string>, unavailableDays: Array<{ day: string, reason: string }> } } }>}
 */
export function appointmentSuggestionsDays(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query appointmentSuggestionsDays(
          $filter: AppointmentSuggestionsDaysFilterInput!
        ) {
          appointmentSuggestionsDays(filter: $filter) {
            days
            unavailableDays {
              day
              reason
            }
          }
        }
      `,
      operationName: "appointmentSuggestionsDays",
      variables,
    },
    opts,
  );
}

/**
 * Load company tags for a location
 * @param {{ location_id: string }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { company: ({ id: string, tags: Array<{ group: string, value: string }> }) | null } }>}
 */
export function loadCompanyTags(variables, context) {
  return loadGraphql(
    {
      query: gql`
        query loadCompanyTags($location_id: ID!) {
          company(location_id: $location_id) {
            id
            tags {
              group
              value
            }
          }
        }
      `,
      operationName: "loadCompanyTags",
      variables,
    },
    null,
    context,
  );
}

/**
 * Replace a company tag group
 * @param {ReplaceCompanyTagGroupInput} input
 * @returns {Promise<{ data: { replaceCompanyTagGroup: JSON | null } }>}
 */
export function replaceCompanyTagGroup(input) {
  return loadGraphql({
    query: gql`
      mutation replaceCompanyTagGroup($input: ReplaceCompanyTagGroupInput!) {
        replaceCompanyTagGroup(input: $input)
      }
    `,
    operationName: "replaceCompanyTagGroup",
    variables: { input },
  });
}

/**
 * Load legal guardians for a client
 * @param {{ client_id: string }} variables
 * @returns {Promise<{ data: { legalGuardians: Array<{ id: string, first_name: string, last_name: string, birth_number?: string, phone?: string, email?: string }> } }>}
 */
export async function loadLegalGuardians(variables) {
  return loadGraphql({
    query: `
      query legalGuardians($client_id: ID!) {
        legalGuardians(client_id: $client_id) {
          id
          first_name
          last_name
          birth_number
          phone
          email
        }
      }
    `,
    operationName: "legalGuardians",
    variables,
  });
}

/**
 * Load client activity logs
 * @param {{ client_id: string }} variables
 * @returns {Promise<{ data: { clientActivityLogs: Array<{ id: string, activity_type: string, activity_reference?: string, description?: string, content?: string, attachment?: string, created_at: string }> } }>}
 */
export function loadClientActivityLogs(variables) {
  return loadGraphql({
    query: `
      query clientActivityLogs($client_id: ID!) {
        clientActivityLogs(client_id: $client_id) {
          id
          activity_type
          activity_reference
          description
          content
          attachment
          created_at
        }
      }
    `,
    operationName: "clientActivityLogs",
    variables: variables,
  });
}

/**
 * Load a specific client activity log by id
 * @param {string} clientId
 * @returns {Promise<{ data: { clientActivityLog: ({ id: string, activity_type: string, activity_reference?: string, description?: string, content?: string, created_at: string }) | null } }>}
 */
export function loadClientActivityLog(clientId) {
  return loadGraphql({
    query: `
      query clientActivityLog($id: ID!) {
        clientActivityLog(clientId: $id) {
          id
          activity_type
          activity_reference
          description
          content
          created_at
        }
      }
    `,
    operationName: "clientActivityLog",
    variables: {
      id: clientId,
    },
  });
}

/**
 * Create a client activity log
 * @param {CreateClientActivityLogInput} payload
 * @returns {Promise<{ data: { createClientActivityLog: { id: string, client_id: string, activity_type: string, description?: string, content?: string } } }>}
 */
export function createClientActivityLog(payload) {
  return loadGraphql({
    query: `
      mutation createClientActivityLog($input: CreateClientActivityLogInput!) {
        createClientActivityLog(input: $input) {
          id
          client_id
          activity_type
          description
          content
        }
      }
    `,
    operationName: "createClientActivityLog",
    variables: { input: payload },
  });
}

/**
 * Load available employees
 * @param {{ page?: PageInput, filter?: EmployeeFilter, order?: EmployeeOrder }} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { employees: GqlList<{ id: string, first_name: string, last_name: string, available?: boolean }> } }>}
 */
export function availableEmployees(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query employees(
          $page: PageInput
          $filter: EmployeeConnectionFilterInput
          $order: EmployeeConnectionOrderInput
        ) {
          employees(page: $page, filter: $filter, order: $order) {
            data {
              id
              first_name
              last_name
              available
            }
          }
        }
      `,
      operationName: "availableEmployees",
      variables,
    },
    opts,
  );
}

/**
 * Load available floor manager nurses
 * @param {{ page?: PageInput, filter?: EmployeeFilter, order?: EmployeeOrder, location_id: string, day: string, now: string }} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { employees: GqlList<{ id: string, first_name: string, last_name: string, available?: boolean, breaks: Array<{ from: string, to: string }>, times_spent: Array<{ from: string, to: string }> }> } }>}
 */
export function availableFloorManagerNurses(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query employees(
          $page: PageInput
          $filter: EmployeeConnectionFilterInput
          $order: EmployeeConnectionOrderInput
          $location_id: ID!
          $day: String!
          $now: String!
        ) {
          employees(page: $page, filter: $filter, order: $order) {
            data {
              id
              first_name
              last_name
              available
              breaks(at: $now) {
                from
                to
              }
              times_spent(day: $day, location_id: $location_id) {
                from
                to
              }
              status(day: $day)
            }
          }
        }
      `,
      operationName: "availableFloorManagerNurses",
      variables,
    },
    opts,
  );
}

/**
 * Load location working hours
 * @param {{ location_id: string }} variables
 * @returns {Promise<{ data: { location: ({ id: string, working_hours: Array<{ valid_from: string, valid_to: string, from: string, to: string }> }) | null } }>}
 */
export function loadWorkingHours(variables) {
  return loadGraphql({
    query: `query loadWorkingHours($location_id: ID!) {
      location(id: $location_id) {
        id
        working_hours {${workingHours("valid_from valid_to from to")}}
      }
    }`,
    operationName: "loadWorkingHours",
    variables: variables,
  });
}

/**
 * Load floor manager employees view
 * @param {{ location_id: string, now: string, day: string, employeeFilter?: EmployeeFilter }} variables
 * @param {Context} context
 * @returns {Promise<{ data: { location: { id: string, working_hours: Array<{ valid_from: string, valid_to: string, from: string, to: string }> } | null, employees: GqlList<{ id: string, first_name: string, last_name: string, available?: boolean, times_spent: Array<{ from: string, to: string }>, shift: { clinis_position?: string, from?: string, to?: string } | null, breaks: Array<{ from: string, to: string }> }> } }>}
 */
export function loadFloorManagerEmployees(variables, context) {
  return loadGraphql(
    {
      query: gql`query loadFloorManagerEmployees($location_id: ID!, $now: String!, $day: String!, $employeeFilter: EmployeeConnectionFilterInput) {
      location(id: $location_id) {
        id
        working_hours {${workingHours("valid_from valid_to from to")}}
      }
      employees(filter: $employeeFilter) {
        data {
          id
          first_name
          last_name
          available
          roles
          times_spent(day: $day, location_id: $location_id) {
            from
            to
          }
          shift(at: $now) {
            from
            to
          }
          breaks(at: $now) {
            from
            to
          }
          status(day: $day)
        }
      }
    }`,
      operationName: "loadFloorManagerEmployees",
      variables: variables,
    },
    null,
    context,
  );
}

/**
 * Fetch the next appointment for a client
 * @param {string} clientId
 * @returns {Promise<{ data: { appointments: GqlList<{ id: string, start: string, calendar_id: string, status_id: number, service: { id: string, name: string }, client: { first_name: string, last_name: string } }> } }>}
 */
export async function getNextAppointmentForClient(clientId) {
  const now = new Date();
  const todayStart = new Date(now.setHours(0, 0, 0, 0)).toISOString();

  return loadGraphql({
    query: `
      query getNextAppointmentForClient($client_id: String!, $todayStart: String!) {
        appointments(
          filter: {
            client_id: { eq: $client_id }
            start: { gte: $todayStart } # Include past and ongoing appointments today
          }
          order: { asc: start }
        ) {
          data {
            id
            start
            calendar_id
            status_id
            service {
              id
              name
            }
            client {
              first_name
              last_name
            }
          }
        }
      }
    `,
    operationName: "getNextAppointmentForClient",
    variables: {
      client_id: clientId,
      todayStart, // Fetches all appointments today, even past ones
    },
  });
}

/**
 * Fetch a single service template by ID
 * @param {string} id
 * @returns {Promise<{ data: { serviceTemplateById: ({ id: string, version: number, template: JSON, service_id: string }) | null } }>}
 */
export async function loadServiceTemplateById(id) {
  return loadGraphql({
    query: `
      query ServiceTemplateById($id: ID!) {
        serviceTemplateById(id: $id) {
          id
          version
          template
          service_id
        }
      }
    `,
    operationName: "ServiceTemplateById",
    variables: {
      id,
    },
  });
}

/**
 * Fetch a service template by service ID
 * @param {string} id
 * @returns {Promise<{ data: { templateByServiceId: ({ id: string, version: number, template: JSON, service_id: string }) | null } }>}
 */
export async function fetchTemplateByServiceId(id) {
  return loadGraphql({
    query: `
      query templateByServiceId($id: ID!) {
        templateByServiceId(id: $id) {
          id
          version
          template
          service_id
        }
      }
    `,
    operationName: "templateByServiceId",
    variables: {
      id,
    },
  });
}

/**
 * Sync employee shifts
 * @returns {Promise<{ data: { syncEmployeeShifts: boolean } }>}
 */
export function syncEmployeeShifts() {
  return loadGraphql({
    query: `mutation syncEmployeeShifts { syncEmployeeShifts }`,
    operationName: "syncEmployeeShifts",
    variables: {},
  });
}

/**
 * Sync available employees
 * @returns {Promise<{ data: { syncAvailableEmployees: boolean } }>}
 */
export function syncAvailableEmployees() {
  return loadGraphql({
    query: "mutation syncAvailableEmployees { syncAvailableEmployees }",
    operationName: "syncAvailableEmployees",
    variables: {},
  });
}

export function syncExistingEmployees() {
  return loadGraphql({
    query: "mutation syncExistingEmployees { syncExistingEmployees }",
    operationName: "syncExistingEmployees",
    variables: {},
  });
}

/**
 * Create a new service template
 * @param {CreateServiceTemplateInput} input
 * @returns {Promise<{ data: { createServiceTemplate: { id: string, version: number, template: JSON, service_id: string } } }>}
 */
export async function createServiceTemplate(input) {
  return loadGraphql({
    query: `
      mutation CreateServiceTemplate($input: CreateServiceTemplateInput!) {
        createServiceTemplate(input: $input) {
          id
          version
          template
          service_id
        }
      }
    `,
    operationName: "CreateServiceTemplate",
    variables: {
      input,
    },
  });
}

/**
 * Update a service template
 * @param {PatchServiceTemplateInput} input
 * @returns {Promise<{ data: { patchServiceTemplate: { id: string, version: number, template: JSON, service_id: string } } }>}
 */
export function patchServiceTemplate(input) {
  return loadGraphql({
    query: `
      mutation patchServiceTemplate($input: PatchServiceTemplateInput!) {
        patchServiceTemplate(input: $input) {
          id
          version
          template
          service_id
        }
      }
    `,
    operationName: "patchServiceTemplate",
    variables: { input },
  });
}

/**
 * Create service
 * @param {CreateServiceInput} input
 * @returns {Promise<{ data: { createService: { id: string, name: string, color?: string } } }>}
 */
export function createService(input) {
  return loadGraphql({
    query: `
    mutation createService($input: CreateServiceInput!) {
      createService(input: $input) {
        id
        name
        color
      }
    }
  `,
    operationName: "createService",
    variables: { input },
  });
}

/**
 * Load services by IDs
 * @param {string[]} serviceIds
 * @returns {Promise<{ data: { services: Array<{ id: string, name: string, color?: string, type?: string }>} }>}
 */
export async function loadServicesByIds(serviceIds) {
  if (!serviceIds || serviceIds.length === 0) {
    return { data: { services: [] } };
  }

  try {
    const servicePromises = serviceIds.map((serviceId) =>
      loadGraphql({
        query: `
          query service($id: ID!) {
            service(id: $id) {
              id
              name
              color
              type
            }
          }
        `,
        operationName: "loadServiceById",
        variables: { id: serviceId },
      }),
    );

    const serviceResponses = await Promise.all(servicePromises);

    const services = serviceResponses
      .map((response) => response?.data?.service)
      .filter((service) => service !== null);

    return { data: { services } };
  } catch (error) {
    console.error("Error loading services:", error);
    return { data: { services: [] } };
  }
}

export function loadReservableServices(variables, opts, context) {
  return loadGraphql(
    {
      query: gql`
        query loadReservableServices($filter: ServiceConnectionFilterInput) {
          services(filter: $filter) {
            page {
              limit
              offset
            }
            count
            data {
              id
              name
              color
              type
              create_reservation
            }
          }
        }
      `,
      operationName: "loadReservableServices",
      variables,
    },
    opts,
    context,
  );
}

/**
 * Soft delete a service
 * @param {string} id
 * @returns {Promise<{ data: { softDeleteService: { id: string, name: string, visible: boolean } } }>}
 */
export function softDeleteService(id) {
  return loadGraphql({
    query: `
      mutation softDeleteService($id: ID!) {
        softDeleteService(id: $id) {
          id
          name
          visible
        }
      }
    `,
    operationName: "softDeleteService",
    variables: { id },
  });
}

/**
 * Load appointment data for cleaning page
 * @param {{ appointmentId: string }} variables
 * @returns {Promise<{ data: { appointments: GqlList<{ id: string, start: string, end: string, status_id: number, active_step: AppointmentStep, client: { id: string, first_name: string, last_name: string } }> } }>}
 */
export function loadCleaningAppointment(variables) {
  return loadGraphql({
    query: gql`
      query cleaningAppointment($appointmentId: String!) {
        appointments(filter: { id: { eq: $appointmentId } }) {
          data {
            id
            start
            end
            status_id
            active_step
            client {
              id
              first_name
              last_name
            }
            employee {
              first_name
              last_name
            }
            actual_calendar_id
          }
        }
      }
    `,
    operationName: "cleaningAppointment",
    variables,
  });
}

export function loadAppointmentClientData(variables) {
  return loadGraphql({
    query: gql`
      query appointmentClientData(
        $clientId: String!
        $locationId: ID!
        $startOfDay: String!
        $endOfDay: String!
        $now: String!
        $nameDaysFilter: NameDayConnectionFilterInput
        $doctorFilter: EmployeeConnectionFilterInput
      ) {
        appointments(
          filter: {
            client_id: { eq: $clientId }
            start: { gte: $startOfDay, lte: $endOfDay }
          }
          order: { asc: start }
        ) {
          data {
            id
            start
            end
            status_id
            active_step
            adhoc_type

            client {
              id
              labs_id
              first_name
              last_name
              email
              birth_number
              birthdate
              phone
              phone_prefix
              street
              city
              post_code
              correspondence_street
              correspondence_city
              correspondence_post_code
              location_id
              prefs
              risks
              medicaments
              sex
              blocker
              blockers {
                text
                file {
                  type
                  objectid
                  file
                  details { name }
                }
              }
              doctors {
                id
                first_name
                last_name
              }
              profile_photo {
                type
                objectid
                file
              }
              any_doctor
              contacts {
                id
                type
                first_name
                last_name
                email
                birthdate
                street
                city
                post_code
                phone_prefix
                phone
                pending_changes
                note
                primary
              }
              pending_changes
              insurance_code
              importantInfoTags: companyTags(
                filter: { group: { eq: "${companyTagGroup.IMPORTANT_INFO}" } }
              ) {
                value
              }
              languageInfoTags: companyTags(
                filter: { group: { eq: "${companyTagGroup.LANGUAGES}" } }
              ) {
                value
              }
              location { name }
              coordinator {
                first_name
                last_name
              }

              treatments {
                id
                type
                subtype
                problematic
                note
                start
                end
                expires_at
                refinements_count
                product { name }
                virtualCareInfo { active email }
                state
                product_id
                pickup
                dispensation
                note
                next_retainer_status
                next_retainer_payments
                last_retainer_at
                last_retainer_dispensation_at
                plans {
                  id
                  type
                  state
                  note
                  link
                  retainerInfo {
                    last
                    frequency
                    rubber_bands_note
                  }
                  ipr {
                    ipr
                    info
                  }
                }
              }
              problematicTreatments: treatments(
                filter: { problematic: { eq: true }, end: { null: true }, expires_at: { gt: $now } }
              ) {
                plans { link }
              }
            }

            service {
              id
              name
              type
              color
              locationService(location_id: $locationId) {
                duration
                real_duration
                requires_rentgen
                actual_calendar
              }
            }

            employee {
              id
              first_name
              last_name
            }

            audit {
              data {
                created_at
                changed_fields
                action
                row_data
              }
            }
          }
        }

        nameDays(filter: $nameDaysFilter) {
          data { name }
        }

        doctors: employees(filter: $doctorFilter, order: { asc: last_name }) {
          data {
            id
            first_name
            last_name
          }
        }

        lastAppointments: appointments(
          filter: {
            client_id: { eq: $clientId }
            status_id: { in: [${status.TREATED}, ${status.FINISHED}] }
          }
          page: { offset: 0, limit: 1 }
          order: { desc: treated }
        ) {
          data {
            id
            start
            service { name }
            employeeAssignmentAudits: audit(
              filter: {
                and: [
                  { changed_fields: { name: "employee_id", expr: { null: false } } }
                  {
                    or: [
                      { changed_fields: { name: "status_id", expr: { eq: "${status.ASSIGNED}" } } }
                      { row_data: { name: "status_id", expr: { eq: "${status.ASSIGNED}" } } }
                    ]
                  }
                ]
              }
              order: { desc: created_at }
              page: { offset: 0, limit: 1 }
            ) {
              data { changed_fields }
            }
          }
        }

        braceProducts: products(filter: {id: {eq: "59b7da94-8ce7-4bd1-bdce-f981a72c421e"} client_id: {eq: $clientId}}) {
          count
        }
      }
    `,
    operationName: "appointmentClientData",
    variables: variables,
  });
}

export function loadContactEditData(variables, opts) {
  return loadGraphql(
    {
      query: gql`query loadContactEditData($location_id: ID!) {
        company(location_id: $location_id) {
          contactTypeTags: tags(filter: { group: { eq: "${companyTagGroup.CONTACT_TYPES}" } }) {
            id: value
            name: value
          }
        }
      }`,
      variables,
      operationName: "loadContactEditData",
    },
    opts,
  );
}

/**
 * Load client edit data
 * @param {{ id: string, location_id: string, coordinatorFilter?: EmployeeFilter, doctorFilter?: EmployeeFilter }} variables
 * @returns {Promise<{ data: { client: ({ id: string, first_name: string, last_name: string, birth_number?: string, birthdate?: string, city?: string, street?: string, post_code?: string, correspondence_city?: string, correspondence_street?: string, correspondence_post_code?: string, location_id: string, prefs?: JSON, insurance_code?: string, risks: string, medicaments: string, importantInfoTags: Array<{ id: string }>, languageInfoTags: Array<{ id: string }> }) | null, company: { locations: Array<{ id: string, name: string }>, importantInfoTags: Array<{ id: string, name: string }>, languageInfoTags: Array<{ id: string, name: string }> }, coordinators: GqlList<{ id: string, first_name: string, last_name: string }>, doctors: GqlList<{ id: string, first_name: string, last_name: string }> } }>}
 */
export function loadClientEditData(variables) {
  return loadGraphql({
    query: gql`
      query loadClientEditData($id: ID!, $location_id: ID!, $coordinatorFilter: EmployeeConnectionFilterInput, $doctorFilter: EmployeeConnectionFilterInput) {
        client(id: $id) {
          id
          first_name
          last_name
          birth_number
          birthdate
          city
          street
          post_code
          correspondence_city
          correspondence_street
          correspondence_post_code
          location_id
          prefs
          insurance_code
          risks
          medicaments
          any_doctor
          coordinator_id
          doctors {
            id
          }
          importantInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.IMPORTANT_INFO}" } }) {
            id
          }
          languageInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.LANGUAGES}" } }) {
            id
          }
        }
        company(location_id: $location_id) {
          locations {
            id
            name
          }
          importantInfoTags: tags(filter: { group: { eq: "${companyTagGroup.IMPORTANT_INFO}" } }) {
            id
            name: value
          }
          languageInfoTags: tags(filter: { group: { eq: "${companyTagGroup.LANGUAGES}" } }) {
            id
            name: value
          }
        }
        coordinators: employees(filter: $coordinatorFilter order: {asc: last_name}) { data { id first_name last_name } }
        doctors: employees(filter: $doctorFilter order: {asc: last_name}) { data { id first_name last_name } }
      }
    `,
    operationName: "loadClientEditData",
    variables,
  });
}

/**
 * Patch client
 * @param {PatchClientInput} payload
 * @returns {Promise<{ data: { patchClient: JSON } }>}
 */
export function patchClient(payload) {
  return loadGraphql({
    query: gql`
      mutation patchClient($input: PatchClientInput!) {
        patchClient(input: $input)
      }
    `,
    operationName: "patchClient",
    variables: { input: payload },
  });
}

export function createContact(payload) {
  return loadGraphql({
    query: gql`
      mutation createContact($input: CreateContactInput!) {
        createContact(input: $input)
      }
    `,
    operationName: "createContact",
    variables: { input: payload },
  });
}

/**
 * @param {PatchContactInput} payload
 */
export function patchContact(payload) {
  return loadGraphql({
    query: gql`
      mutation patchContact($input: PatchContactInput!) {
        patchContact(input: $input)
      }
    `,
    operationName: "patchContact",
    variables: { input: payload },
  });
}

export function loadClientFullData(variables, context) {
  return loadGraphql(
    {
      query: gql`
      query clientFullData(
        $id: ID!
        $now: String!
        $nameDaysFilter: NameDayConnectionFilterInput
        $lastAppointmentFilter: AppointmentConnectionFilterInput!
      ) {
        client(id: $id) {
          id
          labs_id
          first_name
          last_name
          email
          birth_number
          birthdate
          phone
          phone_prefix
          street
          city
          post_code
          correspondence_street
          correspondence_city
          correspondence_post_code
          location_id
          prefs
          risks
          medicaments
          sex
          blocker
          blockers {
            text
            file {
              type
              objectid
              file
              details {
                name
              }
            }
          }
          doctors {
            id
            first_name
            last_name
          }
          profile_photo {
            type
            objectid
            file
          }
          any_doctor
          contacts {
            id
            type
            first_name
            last_name
            email
            birthdate
            street
            city
            post_code
            phone_prefix
            phone
            pending_changes
            note
            primary
            created_at
          }
          pending_changes
          insurance_code
          importantInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.IMPORTANT_INFO}" } }) {
            value
          }
          languageInfoTags: companyTags(filter: { group: { eq: "${companyTagGroup.LANGUAGES}" } }) {
            value
          }
          location {
            name
          }
          coordinator {
            first_name
            last_name
          }
          treatments {
            id
            type
            subtype
            problematic
            note
            start
            end
            expires_at
            refinements_count
            state
            product_id
            pickup
            dispensation
            note
            next_retainer_status
            next_retainer_payments
            last_retainer_at
            last_retainer_dispensation_at
            product {
              name
            }
            virtualCareInfo {
              active
              email
            }
            plans {
              id
              type
              state
              note
              link
              retainerInfo {
                last
                frequency
                rubber_bands_note
              }
              ipr {
                ipr
                info
              }
            }
          }
          problematicTreatments: treatments(filter: {problematic: {eq: true} end: {null: true} expires_at: {gt: $now}}) {
            plans {
              link
            }
          }
        }
        nameDays(filter: $nameDaysFilter) {
          data {
            name
          }
        }
        lastAppointments: appointments(
            filter: $lastAppointmentFilter
            page: { offset: 0, limit: 1 }
            order: { desc: treated }
          ) {
            data {
              id
              start
              service {
                name
              }
              employeeAssignmentAudits: audit(
                filter: {
                  and: [
                    {
                      changed_fields: {
                        name: "employee_id"
                        expr: { null: false }
                      }
                    }
                    {
                      or: [
                        {
                          changed_fields: { name: "status_id", expr: { eq: "${status.ASSIGNED}" } }
                        },
                        {
                          row_data: { name: "status_id", expr: { eq: "${status.ASSIGNED}" } }
                        }
                      ]
                    }
                  ]
                }
                order: { desc: created_at }
                page: { offset: 0, limit: 1 }
              ) {
                data {
                  changed_fields
                }
              }
            }
          }
      }
    `,
      operationName: "clientFullData",
      variables,
    },
    null,
    context,
  );
}

/**
 * Fetch appointment notes by appointment ID
 * @param {string} appointmentId
 * @returns {Promise<{ data: { appointmentNotes: Array<{ id: string, note_type: string, note_content: string, doctor_required?: boolean, service_id?: string, reservation_count?: number, reservation_unit?: string, created_at: string, updated_at?: string, created_by_name?: string, updated_by_name?: string, created_by_employee_name?: string, created_by_role?: string }> } }>}
 */
export function fetchAppointmentNotes(appointmentId) {
  return loadGraphql({
    query: gql`
      query FetchAppointmentNotes($appointment_id: ID!) {
        appointmentNotes(appointment_id: $appointment_id) {
          id
          appointment_id
          note_type
          note_content
          doctor_required
          service_id
          reservation_count
          reservation_unit
          created_at
          updated_at
          created_by_name
          updated_by_name
          created_by_employee_name
          created_by_role
          receptionist_versions {
            version_id
            version_number
            is_original
            is_latest
            note_content
            doctor_required
            service_id
            reservation_count
            reservation_unit
            edited_at
            edited_by
            edited_by_name
            edited_by_role
            is_editable
          }
        }
      }
    `,
    operationName: "FetchAppointmentNotes",
    variables: { appointment_id: appointmentId },
  });
}

export async function fetchAppointmentMetadata(clientId) {
  return loadGraphql({
    query: gql`
      query FetchAppointmentMetadata($client_id: ID!) {
        appointmentMetadata(client_id: $client_id) {
          id
          event_type
          timestamp
          appointment_id
          status_id
          rescheduled_from_id
          cancellation_reason
          cancellation_type
          appointment_start
          user_name
          employee_name
          employee_role
          raw_data
          original_service_name
          original_appointment_date
          service_name
        }
      }
    `,
    operationName: "FetchAppointmentMetadata",
    variables: { client_id: clientId },
  });
}

/**
 * Create an appointment note
 * @param {CreateAppointmentNoteInput} input
 * @returns {Promise<{ data: { createAppointmentNote: { id: string, note_type: string, note_content: string, doctor_required?: boolean, service_id?: string, reservation_count?: number, reservation_unit?: string } } }>}
 */
export function createAppointmentNote(input) {
  return loadGraphql({
    query: gql`
      mutation CreateAppointmentNote($input: CreateAppointmentNoteInput!) {
        createAppointmentNote(input: $input) {
          id
          note_type
          note_content
          doctor_required
          service_id
          reservation_count
          reservation_unit
        }
      }
    `,
    operationName: "CreateAppointmentNote",
    variables: { input },
  });
}

export function createEditedAppointmentNote({ previous_id, input }) {
  return loadGraphql({
    query: `
      mutation CreateEditedAppointmentNote($previous_id: ID!, $input: EditAppointmentNoteInput!) {
        createEditedAppointmentNote(previous_id: $previous_id, input: $input) {
          id
          appointment_id
          note_type
          note_content
          doctor_required
          service_id
          reservation_count
          reservation_unit
        }
      }
    `,
    operationName: "CreateEditedAppointmentNote",
    variables: { previous_id, input },
  });
}

/**
 * Create a new filled service template
 * @param {CreateFilledServiceTemplateInput} input
 * @returns {Promise<{ data: { createFilledServiceTemplate: { id: string, appointment_id: string, template_id: string, filled_data: JSON } } }>}
 */
export async function createFilledServiceTemplate(input) {
  return loadGraphql({
    query: gql`
      mutation CreateFilledServiceTemplate(
        $input: CreateFilledServiceTemplateInput!
      ) {
        createFilledServiceTemplate(input: $input)
      }
    `,
    operationName: "CreateFilledServiceTemplate",
    variables: { input },
  });
}

/**
 * Patch an existing filled service template
 * @param {PatchFilledServiceTemplateInput} input
 * @returns {Promise<{ data: { patchFilledServiceTemplate: { id: string, appointment_id: string, template_id: string, filled_data: JSON } } }>}
 */
export async function patchFilledServiceTemplate(input) {
  return loadGraphql({
    query: `
      mutation PatchFilledServiceTemplate($input: PatchFilledServiceTemplateInput!) {
        patchFilledServiceTemplate(input: $input) {
          id
          appointment_id
          template_id
          filled_data
        }
      }
    `,
    operationName: "PatchFilledServiceTemplate",
    variables: { input },
  });
}

export async function fetchMedicalReportsByClientId(clientId) {
  const response = await loadGraphql({
    query: gql`
      query GetMedicalReports($client_id: ID!) {
        medicalReportsByClientId(client_id: $client_id) {
          report_group_id
          report_type
          original_created_at
          employee_name
          employee_role
          versions {
            version_id
            version_number
            is_original
            is_latest
            report_text
            created_at
            edited_at
            edited_by_name
            isEditable
          }
        }
      }
    `,
    operationName: "GetMedicalReports",
    variables: { client_id: String(clientId) },
  });

  return response.data?.medicalReportsByClientId || [];
}

export function loadClientReservationData(variables, opts, context) {
  return loadGraphql(
    {
      query: gql`
        query loadClientReservationData(
          $page: PageInput
          $notesFilter: AppointmentNotes2ConnectionFilterInput
          $notesOrder: AppointmentNotes2ConnectionOrderInput
          $appointmentsFilter: AppointmentConnectionFilterInput!
          $appointmentsOrder: AppointmentConnectionOrderInput
        ) {
          appointmentNotes2(
            page: $page
            filter: $notesFilter
            order: $notesOrder
          ) {
            data {
              id
              service_id
              service {
                id
                product_id
              }
              reservation_unit
              reservation_count
              note_content
              doctor_required
            }
          }
          appointments(
            page: $page
            filter: $appointmentsFilter
            order: $appointmentsOrder
          ) {
            data {
              id
              start
              service {
                name
              }
            }
          }
        }
      `,
      variables,
    },
    opts,
    context,
  );
}

/**
 * Fetch all client notes
 * @param {string|number} clientId
 * @returns {Promise<{ data: { clientNotes: Array<{ id: string, appointment_id?: string, note_type: string, note_content: string, doctor_required?: boolean, service_id?: string, reservation_count?: number, reservation_unit?: string, created_at: string, updated_at?: string, created_by_name?: string, updated_by_name?: string, created_by_employee_name?: string, created_by_role?: string }> } }>}
 */
export function fetchClientNotes(clientId, opts) {
  return loadGraphql(
    {
      query: gql`
        query ClientNotes($client_id: ID!) {
          clientNotes(client_id: $client_id) {
            id
            appointment_id
            note_type
            note_content
            doctor_required
            service_id
            reservation_count
            reservation_unit
            created_at
            updated_at
            created_by_name
            created_by_employee_name
            created_by_role
            service {
              id
              name
              product_id
            }
            receptionist_versions {
              version_id
              version_number
              is_original
              is_latest
              note_content
              doctor_required
              service_id
              reservation_count
              reservation_unit
              edited_at
              edited_by
              edited_by_name
              edited_by_role
              is_editable
              service {
                id
                name
                product_id
              }
            }
          }
        }
      `,
      operationName: "ClientNotes",
      variables: { client_id: clientId },
    },
    opts,
  );
}

/**
 * Update an employee pin
 * @param {string} employeeId
 * @param {string} newPin
 * @returns {Promise<{ data: { changeEmployeePin: boolean } }>}
 */
export async function updateEmployeePin(employeeId, newPin) {
  return loadGraphql({
    query: `
      mutation ChangeEmployeePin($employeeId: ID!, $newPin: String!) {
        changeEmployeePin(employeeId: $employeeId, newPin: $newPin)
      }
    `,
    operationName: "ChangeEmployeePin",
    variables: { employeeId, newPin },
  });
}

/**
 * Get service by name
 * @param {string} name
 * @returns {Promise<{ data: { serviceByName: ({ id: string, name: string, type?: string, color?: string, visible?: boolean, product_id?: string }) | null } }>}
 */
export async function getServiceByName(name) {
  return loadGraphql({
    query: `
      query GetServiceByName($name: String!) {
        serviceByName(name: $name) {
          id
          name
          type
          color
          visible
          product_id
        }
      }
    `,
    operationName: "GetServiceByName",
    variables: { name },
  });
}

/**
 * Fetch ongoing appointments for an employee today
 * @param {string} employeeId
 * @returns {Promise<{ data: { appointments: GqlList<{ id: string, start: string, status_id: number, client: { id: string, first_name: string, last_name: string }, service: { id: string, name: string } }> } }>}
 */
export async function fetchOngoingAppointments(employeeId) {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  return loadGraphql({
    query: `
      query FetchOngoingAppointments($filter: AppointmentConnectionFilterInput!) {
        appointments(filter: $filter) {
          data {
            id
            start
            status_id
            client {
              id
              first_name
              last_name
            }
            service {
              id
              name
            }
          }
        }
      }
    `,
    operationName: "FetchOngoingAppointments",
    variables: {
      filter: {
        employee_id: { eq: employeeId },
        status_id: { in: [2, 3, 20] },
        start: {
          gte: todayStart.toISOString(),
          lte: todayEnd.toISOString(),
        },
      },
    },
  });
}

/**
 * Load next-appointment note history for a client
 * @param {string} clientId
 * @returns {Promise<{ data: { nextAppointmentNoteHistory: Array<{ note: string, edited_by_username?: string, employee_first_name?: string, employee_last_name?: string, last_edited_at?: string, role?: string }> } }>}
 */
export async function loadNextAppointmentNoteHistory(clientId) {
  return loadGraphql({
    query: `
      query NextAppointmentNoteHistory($clientId: ID!) {
        nextAppointmentNoteHistory(clientId: $clientId) {
          note
          edited_by_username
          employee_first_name
          employee_last_name
          last_edited_at
          role
        }
      }
    `,
    operationName: "NextAppointmentNoteHistory",
    variables: { clientId },
  });
}

/**
 * Load current and transient next-appointment notes for a client
 * @param {string} clientId
 * @returns {Promise<{ data: { getNextAppointmentNoteBundle: ({ currentNote: { note: string, edited_by_username?: string, employee_first_name?: string, employee_last_name?: string, last_edited_at?: string, roles?: Array<string>, appointment_id?: string }, transientNotes: Array<{ note: string, edited_by_username?: string, employee_first_name?: string, employee_last_name?: string, last_edited_at?: string, roles?: Array<string>, appointment_id?: string }> }) | null } }>}
 */
export async function loadNextAppointmentNoteBundle(clientId) {
  return loadGraphql({
    query: `
      query GetNextAppointmentNoteBundle($clientId: ID!) {
        getNextAppointmentNoteBundle(clientId: $clientId) {
          currentNote {
            note
            edited_by_username
            employee_first_name
            employee_last_name
            last_edited_at
            roles
            appointment_id
          }
          transientNotes {
            note
            edited_by_username
            employee_first_name
            employee_last_name
            last_edited_at
            roles
            appointment_id
          }
        }
      }
    `,
    operationName: "GetNextAppointmentNoteBundle",
    variables: { clientId },
  });
}

/**
 * Set the client's next-appointment note
 * @param {string} client_id
 * @param {string} note
 * @returns {Promise<{ data: { setNextAppointmentNote: string | null } }>}
 */
export async function setNextAppointmentNote(client_id, note) {
  return loadGraphql({
    query: `
      mutation SetNextAppointmentNote($client_id: ID!, $note: String!) {
        setNextAppointmentNote(client_id: $client_id, note: $note)
      }
    `,
    operationName: "SetNextAppointmentNote",
    variables: { client_id, note },
  });
}

/**
 * Create an edited medical report
 * @param {CreateEditedMedicalReportInput} input
 * @returns {Promise<{ report_id: string, report_created_at: string, report_type: string, report_text: string, client_id: string, previous_id: string | null, employee_id: string, edited_by: string, edited_at: string } | undefined>}
 */
export async function createEditedMedicalReport(input) {
  const response = await loadGraphql({
    query: `
      mutation CreateEditedMedicalReport($input: CreateEditedMedicalReportInput!) {
        createEditedMedicalReport(input: $input) {
          report_id
          report_created_at
          report_type
          report_text
          client_id
          previous_id
          employee_id
          edited_by
          edited_at
        }
      }
    `,
    operationName: "CreateEditedMedicalReport",
    variables: { input },
  });

  return response.data?.createEditedMedicalReport;
}

/**
 * Fetch a file and return an object URL
 * @typedef {{url: string, filename: string, extension: string, contentType: string}} File
 * @param {{ type: number, objectid: string, file: number }} params
 * @returns {Promise<File>}
 */
export async function file({ type, file, objectid }) {
  const authToken = auth.getAuthenticationToken();

  const resp = await fetch(
    `${config.apiUrl}/file?type=${type}&objectid=${objectid}&file=${file}`,
    {
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    },
  );

  const filename = getFilename(resp.headers.get("Content-Disposition"));
  const ext = getFileExtension(filename);

  return {
    url: URL.createObjectURL(await resp.blob()),
    filename: filename,
    extension: ext,
    contentType: resp.headers.get("content-type"),
  };
}

/**
 * @param {{ client_id: string }} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { client: ({ fileCollections: Array<{ id: string, files: Array<{ id: string }> }> }) | null } }>}
 */
export function loadClientDocumentsFileCollections(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query ClientFileCollections($client_id: ID!) {
          client(id: $client_id) {
            fileCollections(filter: { view: { eq: "documents" } }) {
              id
              created_at
              type
              class_name
              state_name
              allow_export
              files {
                id
                name
              }
            }
          }
        }
      `,
      operationName: "ClientFileCollections",
      variables: variables,
    },
    opts,
  );
}

export function loadClientHistoryData(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query loadClientHistoryData(
          $client_id: ID!
          $webhooksFilter: WebhookConnectionFilterInput
        ) {
          client(id: $client_id) {
            historyRecords {
              id
              created_at
              data
              template {
                template
              }
            }
          }
          webhooks(filter: $webhooksFilter) {
            data {
              id
              created_at
              payload
              employee {
                first_name
                last_name
              }
            }
          }
        }
      `,
      variables,
      operationName: "loadClientHistoryData",
    },
    opts,
  );
}

/**
 * @param {{ client_id: string }} variables
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ data: { client: ({ fileCollections: Array<{ id: string, files: Array<{ id: string }> }> }) | null } }>}
 */
export function loadClientGalleryFileCollections(variables, opts) {
  return loadGraphql(
    {
      query: gql`
        query ClientFileCollections($client_id: ID!) {
          client(id: $client_id) {
            fileCollections(filter: { view: { eq: "gallery" } }) {
              id
              created_at
              type
              class_name
              allow_export
              files {
                id
              }
            }
          }
        }
      `,
      operationName: "ClientFileCollections",
      variables: variables,
    },
    opts,
  );
}

/**
 * Get employee by username
 * @param {{ username: string }} params
 * @returns {Promise<{ data: { employeeByUsername: ({ id: string, username: string, first_name: string, last_name: string }) | null } }>}
 */
export const getEmployeeByUsername = ({ username }) => {
  return loadGraphql({
    query: `
      query GetEmployeeByUsername($username: String!) {
        employeeByUsername(username: $username) {
          id
          username
          first_name
          last_name
        }
      }
    `,
    operationName: "GetEmployeeByUsername",
    variables: { username },
  });
};

/**
 * Load calendars visible at a location and the location name
 * @param {string} locationId
 * @returns {Promise<{ resources: Array<{ id: string; name: string; service_type?: string; location_id?: string; visible?: boolean }>; locationName: string | null }>}
 */
export async function loadCalendarsByLocation(locationId) {
  const res = await loadGraphql({
    query: gql`
      query calendarsByLocation(
        $locationId: ID!
        $filter: CalendarConnectionFilterInput
      ) {
        location(id: $locationId) {
          id
          name
        }
        calendars(filter: $filter) {
          data {
            id
            name
            service_type
            location_id
            visible
          }
        }
      }
    `,
    operationName: "calendarsByLocation",
    variables: {
      locationId,
      filter: {
        location_id: { eq: locationId },
        visible: { eq: true },
      },
    },
  });

  if (!res?.data?.location || !Array.isArray(res.data?.calendars?.data)) {
    console.error("Missing calendars or location in GraphQL response:", res);
    return { resources: [], locationName: null };
  }

  return {
    resources: res.data.calendars.data,
    locationName: res.data.location.name,
  };
}

/**
 * Create an edited filled service template (versioned)
 * @param {{
 *   previous_id: string,
 *   appointment_id: string,
 *   template_id: string,
 *   filled_data: JSON
 * }} input
 * @returns {Promise<{ data: { createEditedFilledServiceTemplate: {
 *   id: string,
 *   appointment_id: string,
 *   template_id: string,
 *   filled_data: JSON,
 *   previous_id: string | null
 * } } }>}
 */
export async function createEditedFilledServiceTemplate(input) {
  return loadGraphql({
    query: `
      mutation CreateEditedFilledServiceTemplate($input: CreateEditedFilledServiceTemplateInput!) {
        createEditedFilledServiceTemplate(input: $input) {
          id
          appointment_id
          template_id
          filled_data
          previous_id
        }
      }
    `,
    operationName: "CreateEditedFilledServiceTemplate",
    variables: { input },
  });
}

export async function fetchGroupedFilledServiceTemplatesByClientId(clientId) {
  const apptsRes = await loadGraphql({
    query: gql`
      query GetClientAppointments(
        $client_id: String!
        $start: DateFilterInput!
      ) {
        appointments(
          filter: { client_id: { eq: $client_id }, start: $start }
          order: { desc: start }
        ) {
          data {
            id
            start
            service {
              id
              name
            }
          }
        }
      }
    `,
    operationName: "GetClientAppointments",
    variables: {
      client_id: String(clientId),
      start: { gte: "2000-01-01T00:00:00Z" },
    },
  });

  const appts = apptsRes?.data?.appointments?.data ?? [];
  if (!appts.length) throw new Error("No appointments found for client.");

  const appointmentIds = [];
  const apptInfoById = {};
  for (const a of appts) {
    appointmentIds.push(a.id);
    apptInfoById[a.id] = {
      date: a.start ?? null,
      serviceName: a.service?.name ?? null,
    };
  }

  const groupedRes = await loadGraphql({
    query: gql`
      query GroupedFilledServiceTemplates($appointment_ids: [ID!]!) {
        filledServiceTemplatesByAppointmentIdsGrouped(
          appointment_ids: $appointment_ids
        ) {
          group_id
          appointment_id
          service_name
          appointment_start
          employee_name
          versions {
            version_id
            version_number
            is_original
            is_latest
            template_id
            filled_data
            template
            created_at
            created_by
            created_by_name
            edited_at
            edited_by
            edited_by_name
            is_editable
            privateRoles {
              key
              values {
                id
                first_name
                last_name
              }
            }
          }
        }
      }
    `,
    operationName: "GroupedFilledServiceTemplates",
    variables: { appointment_ids: appointmentIds },
  });

  const groups =
    groupedRes?.data?.filledServiceTemplatesByAppointmentIdsGrouped ?? [];

  return groups.map((g) => {
    const appt = apptInfoById[g.appointment_id] || {};
    return {
      ...g,
      appointment_start: g.appointment_start ?? appt.date ?? null,
      service_name: g.service_name ?? appt.serviceName ?? null,
    };
  });
}

export function employeesByRoleAtLocation(
  { location_id, roleName, page, shiftWindow },
  opts,
) {
  return loadGraphql(
    {
      query: gql`
        query employeesByRoleAtLocation(
          $page: PageInput
          $filter: EmployeeConnectionFilterInput
          $order: EmployeeConnectionOrderInput
        ) {
          employees(page: $page, filter: $filter, order: $order) {
            data {
              id
              first_name
              last_name
              username
              uzis_id
            }
          }
        }
      `,
      operationName: "employeesByRoleAtLocation",
      variables: {
        page: page ?? { limit: 200, offset: 0 },
        filter: {
          roles: { location_id, role: roleName },
          ...(shiftWindow
            ? { shift: { from: { gte: shiftWindow.gte, lt: shiftWindow.lt } } }
            : {}),
        },
        order: { asc: "last_name" },
      },
    },
    opts,
  );
}

export function employeesOnShiftTodayByRole(
  { location_id, roleName, page },
  opts,
) {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  return employeesByRoleAtLocation(
    {
      location_id,
      roleName,
      page,
      shiftWindow: { gte: start.toISOString(), lt: end.toISOString() },
    },
    opts,
  );
}

export function employeeById(id, opts) {
  return loadGraphql(
    {
      query: gql`
        query employee($id: ID!) {
          employee(id: $id) {
            id
            first_name
            last_name
            username
            uzis_id
          }
        }
      `,
      operationName: "employee",
      variables: { id },
    },
    opts,
  );
}

export function exportFiles(payload) {
  return loadGraphql({
    query: gql`
      mutation exportFiles($input: ExportFilesInput!) {
        exportFiles(input: $input)
      }
    `,
    operationName: "exportFiles",
    variables: { input: payload },
  });
}

export function exportMedicalReport(payload) {
  return loadGraphql({
    query: gql`
      mutation exportMedicalReport($input: ExportMedicalReportInput!) {
        exportMedicalReport(input: $input)
      }
    `,
    operationName: "exportMedicalReport",
    variables: { input: payload },
  });
}

export function nextAppointmentNotes(filter, opts) {
  return loadGraphql(
    {
      query: gql`
        query nextAppointmentNotes(
          $filter: NextAppointmentNoteConnectionFilterInput
        ) {
          nextAppointmentNotes(filter: $filter) {
            count
            data {
              id
              created_at
              role
              note
              employee {
                id
                first_name
                last_name
                username
              }
              client_id
              client {
                id
              }
            }
          }
        }
      `,
      operationName: "nextAppointmentNotes",
      variables: { filter },
    },
    opts,
  );
}

export function createNextAppointmentNote({ input }, opts) {
  return loadGraphql(
    {
      query: gql`
        mutation createNextAppointmentNote(
          $input: CreateNextAppointmentNoteInput!
        ) {
          createNextAppointmentNote(input: $input)
        }
      `,
      operationName: "createNextAppointmentNote",
      variables: { input },
    },
    opts,
  );
}
